import type { TUseTurnstileReturn } from '../use-turnstile';

export type TTurnstileFormGate = {
  /** Widget hasn't produced a token yet (idle/loading) — show a loading skeleton. */
  captchaInitializing: boolean;
  /** Cloudflare-side runtime lockout after MAX_ERROR_RETRIES failed attempts. */
  captchaBlocked: boolean;
  /** `NEXT_PUBLIC_TURNSTILE_SITE_KEY` missing at build time — a deploy/config problem. */
  captchaUnconfigured: boolean;
  /**
   * CORE-1795 fix: the Turnstile script/widget failed to load or render (e.g. an
   * ad-blocker or network policy blocking `challenges.cloudflare.com`) — a terminal
   * failure distinct from `captchaBlocked` (Cloudflare's own bot-detection lockout).
   * MUST NOT be folded into `captchaInitializing`: doing so left the form stuck on an
   * infinite loading skeleton with submit permanently disabled and no explanation for
   * any user whose browser/network blocks the script — a full, silent loss of the
   * password-recovery flow for that user. Consumers should surface this the same way
   * they already surface `captchaBlocked` (reusing the `CAPTCHA_ERROR` copy).
   */
  captchaFailedToLoad: boolean;
  /** Token is ready — the only state in which submit should be enabled. */
  isCaptchaReady: boolean;
};

/**
 * Derives the form-facing captcha gate booleans from `TurnstileBridge`'s `onChange`
 * state. Extracted here because this exact derivation was duplicated verbatim across
 * 4 consumers (B2BOrgRecover/B2BRecover/RecoverV2's RecoverForm + B2BOrgResetPassword) —
 * see CLAUDE.md's "move to shared/ when used 3+ times" rule. Does NOT own the widget
 * lifecycle itself (that stays in `useTurnstile`/`TurnstileBridge`); this is purely a
 * presentational derivation so each consumer can keep its own error-surfacing channel
 * (react-hook-form `setError` vs a generic `setGenericError` state).
 */
export const useTurnstileFormGate = (
  turnstile: TUseTurnstileReturn | undefined,
): TTurnstileFormGate => {
  const status = turnstile?.status ?? 'idle';

  return {
    captchaInitializing: status === 'idle' || status === 'loading',
    captchaBlocked: status === 'blocked',
    captchaUnconfigured: status === 'unconfigured',
    captchaFailedToLoad: status === 'error',
    isCaptchaReady: status === 'ready',
  };
};
